import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { SummaryOfTheYearData } from '@agilox/common';
import { SummaryOfTheYearTop } from '@agilox/common';
import { Vehicle } from '@agilox/common';
import { DistancePipe } from '@agilox/common';
import { WeightPipe } from '@agilox/common';
import { SummaryOfTheYearService } from '@agilox/common';
import { CustomNumberPipe } from '@agilox/pipe/custom-number';
import { SecondsPipe } from '@agilox/pipe/seconds';
import { ButtonComponent } from '@agilox/ui';
import { IconModule } from '@agilox/ui';
import { SummaryDownloadPageComponent } from '../summary-download-page/summary-download-page.component';
import { SummaryNavigationComponent } from '../summary-navigation/summary-navigation.component';
import { SummaryOverviewLabelComponent } from '../summary-overview-label/summary-overview-label.component';
import { SummaryStartPageComponent } from '../summary-start-page/summary-start-page.component';
import { MultipleVehiclesSlideComponent } from './multiple-vehicles-slide/multiple-vehicles-slide.component';
import { MultipleVehiclesSmallSlideComponent } from './multiple-vehicles-small-slide/multiple-vehicles-small-slide.component';

@Component({
	selector: 'multiple-vehicles',
	templateUrl: './multiple-vehicles.component.html',
	imports: [
		SummaryNavigationComponent,
		IconModule,
		ButtonComponent,
		SummaryDownloadPageComponent,
		SummaryOverviewLabelComponent,
		SummaryStartPageComponent,
		MultipleVehiclesSlideComponent,
		MultipleVehiclesSmallSlideComponent,
	],
	providers: [SecondsPipe, WeightPipe, DistancePipe],
})
export class MultipleVehiclesComponent implements OnInit, OnDestroy {
	private readonly summaryService = inject(SummaryOfTheYearService);

	private readonly secondsPipe = inject(SecondsPipe);
	private readonly weightPipe = inject(WeightPipe);
	private readonly distancePipe = inject(DistancePipe);
	private readonly customNumberPipe = inject(CustomNumberPipe);

	year = input<number>();
	data = input.required<SummaryOfTheYearData>();
	vehicles = input.required<Vehicle[]>();

	topVehiclesBusyAbs = computed<SummaryOfTheYearTop[]>(() => {
		const top = Object.values(this.data())
			.map((e) => ({ serial: e.serial, value: e.busyAbs }))
			.sort((a, b) => b.value - a.value)
			.slice(0, 3);

		return top.map((entry) => {
			const vehicle = this.vehicles().find((v) => v.serial === entry.serial);
			return {
				serial: entry.serial,
				name: vehicle ? vehicle.name : '',
				value: this.secondsPipe.transform(entry.value, true, true),
			};
		});
	});

	topVehiclesWeightAbs = computed<SummaryOfTheYearTop[]>(() => {
		const top = Object.values(this.data())
			.map((e) => ({ serial: e.serial, value: e.weightAbs }))
			.sort((a, b) => b.value - a.value)
			.slice(0, 3);

		return top.map((entry) => {
			const vehicle = this.vehicles().find((v) => v.serial === entry.serial);
			return {
				serial: entry.serial,
				name: vehicle ? vehicle.name : '',
				value: this.weightPipe.transform(entry.value, false),
			};
		});
	});

	topVehiclesDistanceAbs = computed<SummaryOfTheYearTop[]>(() => {
		const top = Object.values(this.data())
			.map((e) => ({ serial: e.serial, value: e.distanceAbs }))
			.sort((a, b) => b.value - a.value)
			.slice(0, 3);

		return top.map((entry) => {
			const vehicle = this.vehicles().find((v) => v.serial === entry.serial);
			return {
				serial: entry.serial,
				name: vehicle ? vehicle.name : '',
				value: this.distancePipe.transform(entry.value),
			};
		});
	});

	topVehiclesPickupCnt = computed<SummaryOfTheYearTop[]>(() => {
		const top = Object.values(this.data())
			.map((e) => ({ serial: e.serial, value: e.pickupCnt }))
			.sort((a, b) => b.value - a.value)
			.slice(0, 3);

		return top.map((entry) => {
			const vehicle = this.vehicles().find((v) => v.serial === entry.serial);
			return {
				serial: entry.serial,
				name: vehicle ? vehicle.name : '',
				value: this.customNumberPipe.transform(entry.value),
			};
		});
	});

	protected readonly totalSlides = 11;
	currentSlideIndex = signal(0);
	private swiper: ReturnType<typeof setInterval> | null = null;

	ngOnInit() {
		this.swiper = setInterval(() => this.nextSlide(), 3000);
	}

	ngOnDestroy() {
		if (this.swiper !== null) {
			clearInterval(this.swiper);
			this.swiper = null;
		}
	}

	nextSlide(): void {
		if (this.currentSlideIndex() < this.totalSlides - 1) {
			this.currentSlideIndex.set(this.currentSlideIndex() + 1);
		} else {
			if (this.swiper !== null) {
				clearInterval(this.swiper);
				this.swiper = null;
			}
		}
	}

	closeSummary(): void {
		this.summaryService.closeSummary();
		this.currentSlideIndex.set(0);
	}
}
